import { Box, Button, Chip, CircularProgress, LinearProgress, Typography } from "@mui/material";
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Fetch from "../../services/Fetch";
import { progressColor } from "../../helper/progressColor";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import GridMediaCard from "../../components/cards/GridMediaCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PageHero from "../../components/motion/PageHero";
import { useConstants } from "../../hooks/UseConstants";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";

function MyCourses() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [enrollments, setEnrollments] = useState([]);

    useEffect(() => {
        const load = async () => {
            const result = await Fetch(`${host}/student/buy/`, 'GET');
            if (result.status === 200) {
                const data = result.data.data || [];
                setEnrollments(Array.isArray(data) ? data : []);
            }
            setLoading(false);
        };
        load();
    }, [host]);

    if (loading) return <Box className="flex justify-center py-20"><CircularProgress size={70} /></Box>;

    return (
        <Box>
            <PageHero
                title={isAr ? 'دوراتي' : 'My courses'}
                subtitle={isAr
                    ? `تابع تقدمك في ${enrollments.length} دورة مسجّلة`
                    : `Track progress across ${enrollments.length} enrolled courses`}
                icon={<SchoolOutlinedIcon />}
            />
            {enrollments.length === 0 ? (
                <DashboardPanelCard
                    title={isAr ? 'لا توجد دورات بعد' : 'No courses yet'}
                    subtitle={isAr ? 'عندما تشتري دورة ستظهر هنا لتتابع تقدمك وتعود إليها بسرعة.' : 'Courses you purchase will appear here so you can track progress and jump back in quickly.'}
                    className="mx-0"
                >
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {isAr ? 'ابدأ باختيار دورة من صفحة الدورات المتاحة.' : 'Start by choosing a course from the available courses page.'}
                    </Typography>
                    <Button variant="contained" onClick={() => navigate('/courses')} sx={{ bgcolor: '#0D358C', '&:hover': { bgcolor: '#1d4ed8' } }}>
                        {isAr ? 'استعرض الدورات' : 'Browse courses'}
                    </Button>
                </DashboardPanelCard>
            ) : (
                <Box className="grid grid-cols-2 gap-5 max-sm:grid-cols-1 justify-items-center">
                    {enrollments.map((row, i) => {
                        const course = row.course;
                        if (!course?.id) return null;
                        const progress = Number(row.progress_percentage ?? 0);
                        const teacherName = course.teacher
                            ? `${course.teacher.first_name || ''} ${course.teacher.last_name || ''}`.trim()
                            : '';
                        return (
                            <ScrollReveal key={row.id || course.id} delay={i * 70} direction="up" className="w-full">
                            <GridMediaCard
                                image={resolveCourseImage(course)}
                                title={course.name_ar || course.name_en}
                                description={teacherName || undefined}
                                onClick={() => navigate(`/courses/${course.id}`)}
                                footer={
                                    <Box>
                                        {progress >= 100 && (
                                            <Chip size="small" label={isAr ? 'مكتمل' : 'Completed'} color="success" sx={{ mb: 1 }} />
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(progress, 100)}
                                                color={progressColor(progress)}
                                                sx={{
                                                    flex: 1,
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: '#e5e7eb',
                                                    '& .MuiLinearProgress-bar': { bgcolor: '#0D358C' },
                                                }}
                                            />
                                            <Typography variant="caption">{progress}%</Typography>
                                        </Box>
                                    </Box>
                                }
                            />
                            </ScrollReveal>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

export default MyCourses;
